import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';

export const STANDING_ORDER_TYPE_CREDIT = 'Credit';
export const STANDING_ORDER_TYPE_BANK = 'Bank';

/**
 * Nedarim writes 999 payments to mean "until the donor stops". Treated as a number it put the
 * end date 83 years out and turned "14 of 36 paid" into "14 of 1013".
 */
export const UNLIMITED_PAYMENTS = 999;

/**
 * Dates take the reader's locale, the way formatMoney does. This was pinned to 'he-IL' while
 * the amounts beside it followed the language, so an English user read an English page with
 * Hebrew-ordered dates. The locale is passed in rather than read here, because a module-level
 * formatter is built once and would not notice the language changing.
 */
export function formatDate(date: Date, locale: string): string {
  return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * The tooltip of an order the declined-orders check froze (HAT-1447): when, after how many months in
 * a row, between which months, and the last refusal.
 */
export function describeAutoFreeze(
  autoFreeze: NonNullable<NedarimStandingOrderDtoIn['autoFreeze']>,
  locale: string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const at = new Date(autoFreeze.frozenAt);
  const date = isNaN(at.getTime()) ? '' : formatDate(at, locale);
  const month = (value: string | null) => {
    const [year, mm] = (value ?? '').split('-');
    return year && mm ? `${mm}/${year}` : '';
  };
  return autoFreeze.lastError && autoFreeze.from && autoFreeze.to
    ? t('standingOrders.autoFrozenNote', {
      date, count: autoFreeze.months, from: month(autoFreeze.from), to: month(autoFreeze.to), error: autoFreeze.lastError,
    })
    : t('standingOrders.autoFrozenNoteShort', { date, count: autoFreeze.months });
}

export const isUnlimited = (remainingPayments: number | null): boolean =>
  remainingPayments != null && remainingPayments >= UNLIMITED_PAYMENTS;

/**
 * Nedarim sends dates as dd/MM/yyyy. It is not consistent about padding, so a single-digit day
 * or month has to parse too - it used to return null and the row showed an empty cell.
 * Parsing is deliberately manual: `new Date('2026-09-01')` is midnight UTC, which in Israel is
 * the evening before, and that shifted rows across the boundaries of a date filter.
 */
export function parseDate(value: string | null): Date | null {
  if (!value) return null;
  const parts = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!parts) return null;

  const day = +parts[1];
  const month = +parts[2];
  let year = +parts[3];
  if (year < 100) year += 2000;

  const d = new Date(year, month - 1, day);
  if (isNaN(d.getTime())) return null;

  // Rejects 31/02 and friends, which Date would roll forward into March.
  if (d.getDate() !== day || d.getMonth() !== month - 1) return null;

  return d;
}

/** Local-time yyyy-MM-dd. `toISOString()` here would move the day for anyone east of UTC. */
export function formatIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function getCreationDateRaw(row: NedarimStandingOrderDtoIn): Date | null {
  const value = row.type === STANDING_ORDER_TYPE_CREDIT ? row.creationDate
    : row.type === STANDING_ORDER_TYPE_BANK ? row.date
      : null;
  return parseDate(value);
}

export function getPaymentsPaid(row: NedarimStandingOrderDtoIn): number {
  if (row.type === STANDING_ORDER_TYPE_CREDIT) return row.paymentsSucceeded ?? 0;
  if (row.type === STANDING_ORDER_TYPE_BANK) {
    return (row.numberOfBroadcasts ?? 0) - (row.numberOfReturns ?? 0);
  }
  return 0;
}

/**
 * How many payments the order runs to, for "{{paid}} of {{total}}". Paid plus remaining is that
 * number for Nedarim. On an order the accounting system holds (HAT-1135) a payment the bank
 * returned still used its slot - the office counts collected and returned against the order's
 * total - so the total is every collection plus what remains, and the returned ones show as the
 * gap between "paid" and the bar's end rather than shrinking the order.
 */
export function getPaymentsTotal(row: NedarimStandingOrderDtoIn): number | null {
  const remaining = row.remainingPayments;
  if (remaining == null || isUnlimited(remaining)) return null;
  const used = row.mosadSource === 'Apt' && row.type === STANDING_ORDER_TYPE_BANK
    ? (row.numberOfBroadcasts ?? 0)
    : getPaymentsPaid(row);
  return used + Math.max(remaining, 0);
}

export function getTotalPaid(row: NedarimStandingOrderDtoIn): number | null {
  if (row.type === STANDING_ORDER_TYPE_CREDIT) return getPaymentsPaid(row) * row.amount;
  if (row.type === STANDING_ORDER_TYPE_BANK && row.neto != null) {
    const parsed = parseFloat(row.neto);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
}

/**
 * Null when there is no last payment to date: an unlimited order has no end, and an order whose
 * payments are spent ended on the last one rather than on some future date.
 */
export function getEndDate(row: NedarimStandingOrderDtoIn): Date | null {
  const remaining = row.remainingPayments;
  if (remaining == null || isUnlimited(remaining)) return null;

  const nextDate = parseDate(row.nextDate);
  if (!nextDate) return null;

  if (remaining <= 0) return null;

  nextDate.setMonth(nextDate.getMonth() + (remaining - 1));
  return nextDate;
}

/**
 * 'inactive' is not a reading of the charge, it is the absence of one worth reporting: an order
 * that is stopped or finished keeps whatever Nedarim last wrote on it, and a rejection from the
 * month it was stopped in was still being counted, filtered and banner-ed years later.
 */
export type LastChargeStatus = 'succeeded' | 'failed' | 'none' | 'inactive';

/**
 * `awaitingBank` is an accounting-system state only (HAT-1135): the office keyed the order and
 * sent the mandate to the bank, which has not confirmed it yet. Not collecting, not stopped.
 * `cancelled` is one too (HAT-1441): a bank order cancelled from the board stays on the bank board,
 * where a cancelled Nedarim order simply leaves it.
 */
export type ActiveStatus = 'enabled' | 'disabled' | 'ended' | 'awaitingBank' | 'cancelled';

/**
 * The things a coordinator opens the board to find. Not the same shape as the status fields: a
 * failing charge, a card about to run out and a finished order are answers to "what needs me
 * today", and they are read off three different fields. HAT-1447 added the two the office asked
 * for by name: the orders running as they should, and the frozen ones, which until then sat
 * behind «הצג גם לא פעילות» where nobody found them.
 */
export type AttentionFilter = 'ok' | 'failed' | 'expiring' | 'frozen' | 'ended';

/** The pills in the order the board shows them: the healthy ones, the two problems, the stopped ones. */
export const ATTENTION_FILTERS: AttentionFilter[] = ['ok', 'failed', 'expiring', 'frozen', 'ended'];

/** The dot beside each pill, in the colours the rest of the app gives these states. */
export const ATTENTION_DOT_COLOR: Record<AttentionFilter, string> = {
  ok: 'var(--status-approved-border)',
  failed: 'var(--status-rejected-border)',
  expiring: 'var(--status-pending-border)',
  // The colour of «מוקפאת» in the row's status column, apart from the amber of a card about to expire.
  frozen: 'var(--status-pending-completion-border)',
  ended: 'var(--status-cancelled-border)',
};

export const BANK_TRANSACTION_TYPE_RETURN = 'החזרת הוראת קבע';

/**
 * "No error message" used to mean "succeeded", so an order that had never been charged showed a
 * green Succeeded on the day it was created.
 */
export function getLastChargeStatus(row: NedarimStandingOrderDtoIn): LastChargeStatus {
  const reading = readLastCharge(row);

  /*
   * A failure on an order nobody can act on is not a failure worth reporting. A stopped order
   * keeps the rejection text of the charge that stopped it, and it was being counted in the
   * banner, in the pill and in the filter years after anyone could do anything about it - one
   * fund carried 31 "failed" that way. Such a row reports the date of its last charge and
   * counts towards nothing.
   *
   * Only the failure is suppressed, not the reading. An order that ran its payments to the end
   * finished on a charge that went through, and saying so - "הצליח", with the date - is both
   * true and useful; hiding it behind a bare date told the coordinator less than we know.
   */
  if (reading === 'failed' && getActiveStatus(row) !== 'enabled') return 'inactive';

  return reading;
}

/** What the last charge actually came back as, before asking whether anyone can act on it. */
function readLastCharge(row: NedarimStandingOrderDtoIn): 'succeeded' | 'failed' | 'none' {
  if (row.type === STANDING_ORDER_TYPE_CREDIT) {
    if (row.errorMessage) return 'failed';
    return getPaymentsPaid(row) > 0 ? 'succeeded' : 'none';
  }
  if (row.type === STANDING_ORDER_TYPE_BANK) {
    if (row.lastTransactionType === BANK_TRANSACTION_TYPE_RETURN) return 'failed';
    return row.lastTransactionType ? 'succeeded' : 'none';
  }
  return 'none';
}

/**
 * The date the last-charge column shows - and the only value it may sort by.
 *
 * A failed row shows its reason and no date: a rejection is never written to the transactions
 * this date comes from, so the only date we hold for such an order is of the last charge that
 * did go through, and putting that under the word "failed" would date the failure to the day
 * the money arrived. Sorting by it anyway ordered the board by a number the reader cannot see,
 * which is indistinguishable from not being sorted at all - the failures landed between dated
 * rows for reasons nothing on screen could explain.
 *
 * So the column sorts by what it displays, and a row with nothing to display sorts as having
 * nothing: the failures gather at one end. They are not lost there - the banner counts them
 * and the "נכשל" pill filters to them in a click.
 */
export function getVisibleLastChargeDate(row: NedarimStandingOrderDtoIn): Date | null {
  // The accounting system does date a returned collection - it went out on its billing day and the
  // bank sent it back - so an "Apt" row keeps its date (HAT-1135). Nedarim's failed charge has none.
  if (getLastChargeStatus(row) === 'failed' && row.mosadSource !== 'Apt') return null;
  return parseDate(row.lastTransactionDate);
}

/** The calendar date the next-charge column shows — Nedarim's nextDate or the APT projection on the row. */
export function getNextChargeDate(row: NedarimStandingOrderDtoIn): Date | null {
  return parseDate(row.nextDate ?? null);
}

/**
 * Sort order for the next-charge column. Matches TanStack's `sortUndefined: 'last'` plus a date
 * comparator so rows without a date stay at the end in both ascending and descending sorts.
 */
export function compareNextChargeDateSort(
  a: NedarimStandingOrderDtoIn,
  b: NedarimStandingOrderDtoIn,
  desc = false,
): number {
  const dateA = getNextChargeDate(a);
  const dateB = getNextChargeDate(b);
  const aMissing = dateA == null;
  const bMissing = dateB == null;
  if (aMissing || bMissing) {
    if (aMissing && bMissing) return 0;
    return aMissing ? 1 : -1;
  }
  let cmp = dateA.getTime() - dateB.getTime();
  if (desc) cmp *= -1;
  return cmp;
}

/**
 * An order with no payments left is finished, whatever Nedarim still reports for Enabled. The
 * old site showed that as "הסתיימה"; here it read "פעילה" next to "24 of 24 paid".
 */
export function getActiveStatus(row: NedarimStandingOrderDtoIn): ActiveStatus {
  // Cancelled from the board (HAT-1441) stays «בוטלה» after its end date passes, too.
  if (row.aptCancelledFromSite) return 'cancelled';

  const remaining = row.remainingPayments;
  if (remaining != null && !isUnlimited(remaining) && remaining <= 0) return 'ended';

  if (row.type === STANDING_ORDER_TYPE_CREDIT) {
    return row.enabled === 'Enabled' ? 'enabled' : 'disabled';
  }
  /*
   * Nedarim's bank rows never carried Enabled, so a bank order was always "active" here. The
   * accounting system's rows do say when the office stopped one (HAT-1135), and only they set
   * the field on a bank row; a Nedarim bank row keeps reading as before.
   */
  if (row.enabled === 'Disabled') return 'disabled';
  if (row.aptAwaitingBank) return 'awaitingBank';
  return 'enabled';
}

/**
 * The words for an order's activity. HAT-1317 calls a disabled order "מוקפאת" - Nedarim's word,
 * and the name of the action that now produces it - but only where that action exists: until the
 * "standing-order-actions" flag is on for the fund, the board reads exactly as it did.
 */
export function activeStatusKey(status: ActiveStatus, frozenTerm = false): string {
  return status === 'disabled' && frozenTerm
    ? 'standingOrders.active_frozen'
    : `standingOrders.active_${status}`;
}

/**
 * An order the credit board leaves out of its default view (HAT-1443): finished, frozen or
 * cancelled. A coordinator opens the board for the active orders and the rejections; these only
 * crowded them. Since HAT-1447 they come back under the pill that names them, «מוקפא» or
 * «הסתיים», or when the coordinator searches.
 */
export function isInactive(row: NedarimStandingOrderDtoIn): boolean {
  const status = getActiveStatus(row);
  return status === 'ended' || status === 'disabled' || status === 'cancelled';
}

/**
 * Which pill an order answers to (HAT-1447). «תקין» is an active order whose last charge did not
 * fail: charged, or not charged yet. `needsSecondPill` is the board's own second question - a card
 * about to run out on the credit board, payments about to run out on the bank board. On the credit
 * board «הסתיים» also takes the rare order cancelled from the site, which has no pill of its own
 * there and would otherwise never show.
 */
export function matchesAttention(
  row: NedarimStandingOrderDtoIn,
  key: AttentionFilter,
  board: { isBank: boolean; needsSecondPill: (row: NedarimStandingOrderDtoIn) => boolean },
): boolean {
  const status = getActiveStatus(row);
  switch (key) {
    case 'ok': return status === 'enabled' && getLastChargeStatus(row) !== 'failed';
    case 'failed': return getLastChargeStatus(row) === 'failed';
    case 'expiring': return board.needsSecondPill(row);
    case 'frozen': return status === 'disabled';
    case 'ended': return status === 'ended' || (!board.isBank && status === 'cancelled');
  }
}

/**
 * Left out of the credit board's view (HAT-1443, HAT-1447): a frozen order until «מוקפא» is
 * picked, a finished or cancelled one until «הסתיים» is. There used to be one «הצג גם לא פעילות»
 * for both, and the office did not find the frozen orders behind it. A search looks through every
 * order, so a donor's order turns up when someone searches for the donor, frozen or not. The bank
 * board never hid anything.
 */
export function isOutOfView(
  row: NedarimStandingOrderDtoIn,
  attention: ReadonlySet<AttentionFilter>,
  board: { isBank: boolean; searching: boolean },
): boolean {
  if (board.isBank || board.searching) return false;
  const status = getActiveStatus(row);
  if (status === 'disabled') return !attention.has('frozen');
  if (status === 'ended' || status === 'cancelled') return !attention.has('ended');
  return false;
}

/** Counts towards next month's expected income. */
export function isCollecting(row: NedarimStandingOrderDtoIn): boolean {
  return getActiveStatus(row) === 'enabled';
}

/**
 * Nedarim's currency field is "1"/"2" on some endpoints and a symbol on others. An unrecognised
 * value must not silently become shekels - a dollar order shown, and charged, in the wrong
 * currency is money taken incorrectly.
 */
export function getCurrencyCode(currency: string | null): 1 | 2 | null {
  if (currency == null || currency.trim() === '') return 1;

  const value = currency.trim().toLowerCase();
  if (value === '1' || value === 'ils' || value === '₪' || value === 'nis') return 1;
  if (value === '2' || value === 'usd' || value === '$') return 2;
  return null;
}

export const CURRENCY_BY_CODE: Record<1 | 2, string> = { 1: 'ILS', 2: 'USD' };

export function getCurrencyIso(row: NedarimStandingOrderDtoIn): string {
  const code = getCurrencyCode(row.currency);
  return code == null ? 'ILS' : CURRENCY_BY_CODE[code];
}

export function formatMoney(value: number | null, currencyIso: string, locale: string): string {
  if (value == null) return '';
  return value.toLocaleString(locale, { style: 'currency', currency: currencyIso });
}

export function formatRowMoney(
  value: number | null, row: NedarimStandingOrderDtoIn, locale: string
): string {
  return formatMoney(value, getCurrencyIso(row), locale);
}

/**
 * Nedarim send the card expiry as MMYY ("1225" is December 2025); the iframe hands it back as
 * MM/YY, so both are accepted. Returns the last moment the card is still good - the end of
 * that month - because a card marked 12/25 works through the whole of December.
 */
export function parseCardExpiry(validity: string | null): Date | null {
  if (!validity) return null;

  const digits = validity.replace(/\D/g, '');
  if (digits.length !== 4) return null;

  const month = +digits.slice(0, 2);
  const year = 2000 + +digits.slice(2);
  if (month < 1 || month > 12) return null;

  // Day 0 of the next month is the last day of this one.
  return new Date(year, month, 0, 23, 59, 59, 999);
}

/** How the expiry should read on screen: 12/25. */
export function formatCardExpiry(validity: string | null): string | null {
  const digits = validity?.replace(/\D/g, '') ?? '';
  if (digits.length !== 4) return null;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

export type CardExpiryState = 'valid' | 'expiringSoon' | 'expired';

/**
 * A card that runs out is the most common reason a standing order starts failing, and today
 * the fund only finds out from the rejection. Sixty days is enough notice to reach the donor
 * before a payment is missed.
 */
export const EXPIRY_WARNING_DAYS = 60;

export function getCardExpiryState(
  validity: string | null, now: Date = new Date()
): CardExpiryState | null {
  const expiry = parseCardExpiry(validity);
  if (!expiry) return null;

  if (expiry.getTime() < now.getTime()) return 'expired';

  const daysLeft = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  return daysLeft <= EXPIRY_WARNING_DAYS ? 'expiringSoon' : 'valid';
}

/** The card or the bank account the money is taken from, as one line. */
export function getPaymentSourceLabel(row: NedarimStandingOrderDtoIn): string | null {
  if (row.type === STANDING_ORDER_TYPE_CREDIT) {
    return row.lastNum ? `····${row.lastNum}` : null;
  }
  if (row.type === STANDING_ORDER_TYPE_BANK) {
    return row.accountDetails || null;
  }
  return null;
}

/** Sums by currency, so shekels and dollars are never added into one meaningless number. */
export function sumByCurrency(
  rows: NedarimStandingOrderDtoIn[], amountOf: (row: NedarimStandingOrderDtoIn) => number
): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const row of rows) {
    const iso = getCurrencyIso(row);
    totals[iso] = (totals[iso] ?? 0) + amountOf(row);
  }
  return totals;
}
